import React, { useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import SitePreferences from './SitePreferences'
import {
  onFileChange,
  handleUpload,
} from 'store/css-admin/sitePreferences/operations'
import { RootState } from 'types'
import { AppDispatch } from 'store/types'

interface SitePreferencesManagerState {
  file: File | null
  fileName: string
  uploadResults: {
    sitePreferencesResults?: Array<{
      siteId: number | string
      siteName: string
    }>
    errors?: Array<{
      siteId: number | string
      siteName: string
      rowNumber?: number
      error: string
    }>
  } | null
  isLoading: boolean
  error: string | null
}

interface FormattedUploadResult {
  siteId?: number | string
  siteName?: string
  rowNumber?: number
  error?: string
}

const SitePreferencesContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { file, fileName, uploadResults, isLoading, error } = useSelector<
    RootState,
    SitePreferencesManagerState
  >(state => state.sitePreferencesManager)

  const formattedUploadResults = useMemo<
    FormattedUploadResult[] | undefined
  >(() => {
    if (!uploadResults) {
      return undefined
    }

    const { sitePreferencesResults = [], errors = [] } = uploadResults

    const formatted: FormattedUploadResult[] = sitePreferencesResults.map(
      sitePreferencesResult => ({
        siteId: sitePreferencesResult.siteId,
        siteName: sitePreferencesResult.siteName,
      }),
    )

    if (errors.length > 0) {
      errors.forEach(error => {
        formatted.unshift({
          siteId: error.siteId,
          siteName: error.siteName,
          rowNumber: error.rowNumber,
          error: error.error,
        })
      })
    }

    return formatted.length > 0 ? formatted : undefined
  }, [uploadResults])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(onFileChange(event))
    },
    [dispatch],
  )

  const handleUploadClick = useCallback(
    (props: { file?: File | null }) => {
      const fileToUpload = props.file || file
      if (fileToUpload) {
        dispatch(handleUpload({ file: fileToUpload }))
      }
    },
    [dispatch, file],
  )

  return (
    <SitePreferences
      error={error}
      file={file}
      fileName={fileName}
      handleUpload={handleUploadClick}
      isLoading={isLoading}
      onFileChange={handleFileChange}
      formattedUploadResults={formattedUploadResults}
    />
  )
}

export default SitePreferencesContainer
